// @flow
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, intlShape } from 'react-intl';
import classNames from 'classnames/bind';
import { FormValidator } from '../../../utils/form';
import update from 'immutability-helper';
import Select from '../../../elements/Select/Select';
import { orderVerifier } from '../../../utils/string';
import Cleave from 'cleave.js/react';
import Modal from '../../../elements/Modal/Modal';
import AlertContainer from '../../alert/container/AlertContainer';

import style from './HelpTicket.scss';
import formStyle from '../../../assets/styles/form.scss';
import inputStyle from '../../../assets/styles/input.scss';
import buttonStyle from '../../../assets/styles/button.scss';

import type { FormValidationResult } from '../../../utils/form';
import type { Option } from '../../../elements/Select/Select';
import type { TicketRequest } from '../api';
import type { User } from '../../../entities';
import type { Motive } from '../../report/duck';
import type { LocaleState } from '../../locale/duck';

type Props = {
  +locale: LocaleState;
  +motives: any;
  +createTicket: (request: TicketRequest) => void;
  +isCreatingTicket?: boolean;
  +createTicketSuccessful?: boolean;
  +user: User;
  +goToRedirect: (route: string) => void;
};

type State = {
  comments: {
    value: string;
    errors?: string[];
  };
  email: {
    value: string;
    errors?: string[];
  };
  name: {
    value: string;
    errors?: string[];
  };
  modalOpen?: boolean;
  orderId: {
    value: string;
    errors?: string[];
  };
  phone: {
    value: string;
    errors?: string[];
  };
  motive: {
    value: string;
    errors?: string[];
  };
};

const SUBJECT_OPTIONS: Motive[] = [
  {
    type: 'report-motive',
    name: 'order-update',
    translations: {
      'EN': 'Order update',
      'ES': 'Cambio de pedido',
    },
  },
  {
    type: 'report-motive',
    name: 'order-claim',
    translations: {
      'EN': 'I have a claim',
      'ES': 'Tengo un reclamo',
    },
  },
  {
    type: 'report-motive',
    name: 'order-status',
    translations: {
      'EN': 'Order status',
      'ES': 'Estado de mi pedido',
    },
  },
  {
    type: 'report-motive',
    name: 'order-help',
    translations: {
      'EN': 'I need help with my order',
      'ES': 'Necesito ayuda con mi pedido',
    },
  },
];

const initialState = {
  comments: {
    value: '',
  },
  email: {
    value: '',
  },
  name: {
    value: '',
  },
  modalOpen: false,
  orderId: {
    value: '',
  },
  phone: {
    value: '',
  },
  motive: {
    value: '',
  },
};

export default class HelpTicket extends Component<Props, State> {
  state = initialState;
  formValidator: FormValidator;
  phoneCleave: Cleave;

  constructor(props: Props) {
    super(props);
    this.formValidator = new FormValidator({
      comments: {
        required: 'required',
      },
      email: {
        required: 'required',
        emailFormat: 'emailFormat',
      },
      name: {
        required: 'required',
      },
      orderId: {
        orderId: (value) => !orderVerifier(value),
        required: 'required',
      },
      phone: {
        required: 'required',
      },
      motive: {
        required: 'required',
      },
    });
    const { user } = this.props;
    if (user) {
      this.state = {
        ...initialState,
        name: { value: user.firstName + " " + user.lastName },
        email: { value: user.email },
        phone: { value: user.phone },
      };
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { createTicketSuccessful, user } = this.props
    if (!prevProps.createTicketSuccessful && createTicketSuccessful) {
      this.setState({ modalOpen: true });
    }
    if (!prevProps.user && user) {
      this.setState({
        name: { value: user.firstName + " " + user.lastName },
        email: { value: user.email },
        phone: { value: user.phone },
      });
    }
  }

  handleChange = (e: Event) => {
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
      const { name, value } = e.target;
      this.setState({ [name]: { value } });
    }
  };

  handleChangeSubject = (option: Option) => {
    this.setState({
      motive: {
        value: `${option.value}`,
      },
    });
  };

  handleChangePhone = (e: Event) => {
    if (e.target instanceof HTMLInputElement) {
      // $FlowIgnoreLine: raw value not in target unless Cleave makes a flowtyped (or the community)
      const { value, rawValue } = e.target;
      if (value.length <= 15) {
        this.setState({ ['phone']: { value: rawValue } });
        this.phoneCleave.setRawValue(rawValue);
      }
      if (rawValue.length > 15) {
        this.phoneCleave.setRawValue(this.state.phone.value);
      }
    }
  }

  handleFocus = (e: Event) => {
    if (e.target instanceof HTMLInputElement) {
      // $FlowIgnoreLine: raw value not in target unless Cleave makes a flowtyped (or the community)
      const { name, value, rawValue } = e.target;
      if (name === 'phone') {
        if (value.length <= 15) {
          this.setState((state: State) => ({
            [name]: update(state[name], {
              value: { $set: rawValue },
              $unset: ['errors'],
            }),
          }));
          this.phoneCleave.setRawValue(rawValue);
        }
        if (rawValue.length > 15) {
          this.phoneCleave.setRawValue(this.state.phone.value);
        }
      } else {
        this.setState((state: State) => ({
          [name]: update(state[name], { $unset: ['errors'] }),
        }));
      }
    }
  }

  handlePhoneInit = (cleave: any) => {
    this.phoneCleave = cleave;
  }

  handleSend = (e: Event) => {
    const { createTicket } = this.props;
    const {
      email,
      name,
      comments,
      orderId,
      phone,
      motive,
    } = this.state;
    const errors = this.formValidator.validateForm({
      comments: comments.value,
      email: email.value,
      name: name.value,
      orderId: orderId.value,
      phone: phone.value,
      motive: motive.value,
    });
    if (errors.length) {
      errors.forEach((error: FormValidationResult) =>
        this.setState({ [error.field]: { ...this.state[error.field], errors: error.errors } }));
      e.preventDefault();
      return false;
    } else {
      const request: TicketRequest = {
        comments: comments.value,
        email: email.value,
        name: name.value,
        orderId: orderId.value,
        phone: phone.value,
        motive: motive.value,
      };
      createTicket(request);
    }
  };

  handleCloseModal = () => {
    const { isCreatingTicket } = this.props;
    if (!isCreatingTicket) {
      this.setState(initialState);
    }
  }

  render() {
    const { goToRedirect, isCreatingTicket, locale } = this.props;
    const {
      email,
      name,
      comments,
      modalOpen,
      orderId,
      phone,
      motive,
    } = this.state;

    const commentsError = comments.errors && FormValidator.buildErrorLabel(comments.errors, {
      required: 'helpTicket.comments.error.required',
    });
    const emailError = email.errors && FormValidator.buildErrorLabel(email.errors, {
      emailFormat: 'helpTicket.email.error.format',
      required: 'helpTicket.email.error.required',
    });
    const nameError = name.errors && FormValidator.buildErrorLabel(name.errors, {
      required: 'helpTicket.name.error.required',
    });
    const orderIdError = orderId.errors && FormValidator.buildErrorLabel(orderId.errors, {
      orderId: 'helpTicket.orderId.error.orderId',
      required: 'helpTicket.orderId.error.required',
    });
    const phoneError = phone.errors && FormValidator.buildErrorLabel(phone.errors, {
      required: 'helpTicket.phone.error.required',
    });
    const motiveError = motive.errors && FormValidator.buildErrorLabel(motive.errors, {
      required: 'helpTicket.motive.error.required',
    });

    const options = SUBJECT_OPTIONS.map((motive: Motive) => ({
      value: motive.name,
      label: motive.translations[locale.locale.toUpperCase()],
    }));
    return (
      <Fragment>
        <div className={style.alertWrap}>
          <AlertContainer showId="help-ticket"/>
        </div>
        <div className={classNames(style.mainWrap, {
          [style.loading]: isCreatingTicket,
        })}>
          <form
            className={classNames(formStyle.formWrap, style.formWrap)}
            onSubmit={this.handleSend}
          >
            <FormattedMessage id="helpTicket.title">{
              txt => <h2 className={style.title}>{txt}</h2>
            }</FormattedMessage>
            {/*orderId*/}
            <div className={classNames(formStyle.row, formStyle.inputRow)}>
              <FormattedMessage id="helpTicket.orderId.label">{
                txt => <label className={formStyle.label}>{txt}</label>
              }</FormattedMessage>
              <input name="orderId"
                     className={classNames(inputStyle.input,
                       { [inputStyle.red]: orderId.errors && orderId.errors.length })}
                     onChange={this.handleChange}
                     onFocus={this.handleFocus}
                     value={orderId.value}/>
              {orderIdError}
            </div>
            {/*name*/}
            <div className={classNames(formStyle.row, formStyle.inputRow)}>
              <FormattedMessage id="helpTicket.name.label">{
                txt => <label className={formStyle.label}>{txt}</label>
              }</FormattedMessage>
              <input name="name"
                     className={classNames(inputStyle.input,
                       { [inputStyle.red]: name.errors && name.errors.length })}
                     onChange={this.handleChange}
                     onFocus={this.handleFocus}
                     value={name.value}/>
              {nameError}
            </div>
            {/*phone*/}
            <div className={classNames(formStyle.row, formStyle.inputRow)}>
              <FormattedMessage id="helpTicket.phone.label">{
                txt => <label className={formStyle.label}>{txt}</label>
              }</FormattedMessage>
              <Cleave
                name="phone"
                value={phone.value || ''}
                options={{
                  phone: true,
                  phoneRegionCode: 'PE',
                  delimiter: '-',
                }}
                className={classNames(inputStyle.input, {
                  [inputStyle.red]: phone.errors && phone.errors.length,
                  ...style.input,
                })}
                onChange={this.handleChangePhone}
                onFocus={this.handleFocus}
                onInit={this.handlePhoneInit}
              />
              {phoneError}
            </div>
            {/*email*/}
            <div className={classNames(formStyle.row, formStyle.inputRow)}>
              <FormattedMessage id="helpTicket.email.label">{
                txt => <label className={formStyle.label}>{txt}</label>
              }</FormattedMessage>
              <input name="email"
                     className={classNames(inputStyle.input,
                       { [inputStyle.red]: email.errors && email.errors.length })}
                     onChange={this.handleChange}
                     onFocus={this.handleFocus}
                     value={email.value}/>
              {emailError}
            </div>
            {/*motive*/}
            <div className={classNames(formStyle.row, formStyle.inputRow)}>
              <FormattedMessage id="helpTicket.motive.label">{
                txt => <label className={formStyle.label}>{txt}</label>
              }</FormattedMessage>
              <Select options={options}
                      className={classNames({
                        'red': motive.errors && motive.errors.length,
                        'big': true,
                      }, style)}
                      value={motive.value}
                      searchable={true}
                      handleChange={this.handleChangeSubject}/>
              {motiveError}
            </div>
            {/*comments*/}
            <div className={classNames(formStyle.row, formStyle.inputRow)}>
              <FormattedMessage id="helpTicket.comments.label">{
                txt => <label className={formStyle.label}>{txt}</label>
              }</FormattedMessage>
              <textarea name="comments"
                        className={classNames(inputStyle.input,
                          { [inputStyle.red]: comments.errors && comments.errors.length })}
                        onChange={this.handleChange}
                        onFocus={this.handleFocus}
                        value={comments.value}/>
              {commentsError}
            </div>
            <FormattedMessage id="helpTicket.createTicket">{
              txt => <button type="button"
                             onClick={this.handleSend}
                             className={
                               classNames(
                                 buttonStyle.btn,
                                 buttonStyle.lightBlue,
                                 style.button,
                               )}>{txt}</button>
            }</FormattedMessage>
          </form>
        </div>
        <Modal
          open={modalOpen}
          handleClose={this.handleCloseModal}
        >
          <div className={style.modalWrap}>
            <FormattedMessage id="helpTicket.modal.title">{
              txt => <h1 className={style.modalTitle}>{txt}</h1>
            }</FormattedMessage>
            <FormattedMessage id="helpTicket.modal.content">{
              txt => <p className={style.modalContent}>{txt}</p>
            }</FormattedMessage>
            <FormattedMessage id="helpTicket.modal.button">{
              txt =>
                <button
                  className={classNames(
                    buttonStyle.btn,
                    buttonStyle.lightBlue,
                    style.modalButton,
                  )}
                  onClick={() => goToRedirect('/')}
                >{txt}</button>
            }</FormattedMessage>
          </div>
        </Modal>
      </Fragment>
    );
  }
}

HelpTicket.propTypes = {
  locale: PropTypes.object.isRequired,
  motives: PropTypes.array,
  createTicket: PropTypes.func.isRequired,
  isCreatingTicket: PropTypes.bool,
  createTicketSuccessful: PropTypes.bool,
  user: PropTypes.object,
  goToRedirect: PropTypes.func.isRequired,
};

HelpTicket.contextTypes = {
  intl: intlShape,
};



// WEBPACK FOOTER //
// ./src/modules/help-ticket/components/HelpTicket.jsx